import AppRoutes from "@/constants/routes/AppRoutes";
import { getViewConfigFromPath } from "@/config/documentViewConfig";
import type { BreadcrumbConfig } from "@/config/documentViewConfig";
import type Workspace from "@/models/workspace/Workspace";
import {
  getDocumentFolderPath,
  type PathItem,
} from "@/pages/workspace/utils/folderUtils";
import { peekStackBackUrl } from "@/lib/navigation/documentNavigationStack";

export const fromFolderPathSessionKey = (workspaceId: string): string =>
  `fromFolderPath:${workspaceId}`;

/**
 * Builds a workspace files URL from folder path segments (matches Files tab encoding).
 */
export const pathItemsToWorkspaceFilesUrl = (
  workspaceId: string,
  pathItems: PathItem[]
): string => {
  if (pathItems.length === 0) {
    return AppRoutes.workspaceFiles(workspaceId);
  }
  const folderPath = pathItems
    .map((p) => encodeURIComponent(p.name))
    .join("/");
  return AppRoutes.workspaceFilesFolder(workspaceId, folderPath);
};

const folderPathStringToWorkspaceFilesUrl = (
  workspaceId: string,
  folderPath: string
): string => {
  if (!folderPath.trim()) {
    return AppRoutes.workspaceFiles(workspaceId);
  }
  const encoded = folderPath
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
  return AppRoutes.workspaceFilesFolder(workspaceId, encoded);
};

export type ResolveWorkspaceDocumentReturnUrlOptions = {
  workspaceId: string;
  workspace: Workspace | null;
  /** Workspace document id (not blob name). */
  documentId: string | undefined;
};

/**
 * Resolves where Tilbage should go for a workspace document viewer (PDF or HTML).
 */
export const resolveWorkspaceDocumentReturnUrl = ({
  workspaceId,
  workspace,
  documentId,
}: ResolveWorkspaceDocumentReturnUrlOptions): string => {
  const stored = sessionStorage.getItem(fromFolderPathSessionKey(workspaceId));
  if (stored) {
    try {
      const pathItems = JSON.parse(stored) as PathItem[];
      if (Array.isArray(pathItems)) {
        return pathItemsToWorkspaceFilesUrl(workspaceId, pathItems);
      }
    } catch {
      // Fall through to workspace lookup.
    }
  }

  if (workspace && documentId) {
    const folderPath = getDocumentFolderPath(workspace, documentId);
    return folderPathStringToWorkspaceFilesUrl(workspaceId, folderPath);
  }

  return AppRoutes.workspaceFiles(workspaceId);
};

export type ResolveWorkspaceViewerReturnUrlOptions =
  ResolveWorkspaceDocumentReturnUrlOptions & {
    cameFromChat: boolean;
  };

export const resolveWorkspaceViewerReturnUrl = (
  options: ResolveWorkspaceViewerReturnUrlOptions
): string => {
  const stackBack = peekStackBackUrl();
  if (stackBack) {
    return stackBack;
  }
  if (options.cameFromChat) {
    return AppRoutes.chat;
  }
  return resolveWorkspaceDocumentReturnUrl(options);
};

const breadcrumbReturnUrl = (
  breadcrumb: BreadcrumbConfig | BreadcrumbConfig[]
): string => {
  const first = Array.isArray(breadcrumb) ? breadcrumb[0] : breadcrumb;
  return first.url;
};

/**
 * Resolves where Tilbage should go for legislation PDF/HTML viewers.
 */
export const resolveLegislationViewerReturnUrl = (
  pathname: string,
  viewType: "html" | "pdf"
): string => {
  const stackBack = peekStackBackUrl();
  if (stackBack) {
    return stackBack;
  }
  const config = getViewConfigFromPath(pathname, viewType);
  return breadcrumbReturnUrl(config.getBreadcrumbs({}));
};
